import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { actualizarFiltros, establecerPagina } from '../../../redux/slices/pagina'
import { FormSelect } from '../../../components/forms/select/FormSelect'
import { FormInput } from '../../../components/forms/input/FormInput'
import { RootState } from '../../../redux'
import { Offcanvas } from 'react-bootstrap'
import { useRecuperarOpcCatalogoQuery } from '../../../redux/api'
import { AppCodigosCatalogo } from '../../../utils/Constantes'
import { TFormInputs, filtrosBusqueda } from '../models/filtroSitiosForm.type'
import { Filtro } from '../../../models'

type TProps = {
  show: boolean
  onHide: () => void
}

export const OffcanvasFiltrosSitios = ({ onHide, show }: TProps) => {
  const dispatch = useDispatch()

  const filtrosPagina = useSelector((state: RootState) => state.pagina)

  const { register, handleSubmit, setValue, control, reset } = useForm<TFormInputs>({
    mode: 'all',
    defaultValues: {},
  })

  const [envioFormEnter, setEnvioFormEnter] = useState(false)

  const enviarFormEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setEnvioFormEnter(true)
      handleSubmit(onSubmit)

      onHide()
    }
    if (!show && event.key !== 'Enter') {
      setEnvioFormEnter(false)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', enviarFormEnter)
    return () => {
      document.removeEventListener('keydown', enviarFormEnter)
    }
  }, [envioFormEnter])

  const { data: catEstados } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.ESTADOS_REPUBLICA)

  const handleOnEntering = () => {
    // Al abrir el offcanvas, se hidrata el formulario con los filtros del slice global `pagina`.
    // `state.pagina.filtros` se comparte entre pantallas; por eso aqui siempre se parte de reset.
    reset()
    setValue('estado', null)
    setValue('razonSocial', null)
    setValue('nombre', null)
    setValue('rfc', null)
    filtrosPagina.filtros.forEach((filtro) => {
      if (filtrosBusqueda[filtro.clave as keyof TFormInputs].tipo === 'select') {
        setValue(filtro.clave as any, { valor: filtro.valor, etiqueta: filtro.descripcion })
      } else {
        setValue(filtro.clave as any, filtro.valor)
      }
    })
  }

  /**
   * @description Actualiza y aplica filtros de busqueda de sitios
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 11/11/2022
   * @param data Valores del formulario
   */
  const onSubmit: SubmitHandler<TFormInputs> = (data: TFormInputs) => {
    // Contrato: el slice `pagina.filtros` guarda siempre string/number serializables,
    // por eso aqui se normaliza el valor del form (select vs input texto).
    const filtros: Filtro[] = []
    Object.entries(data).forEach((filtro: any) => {
      filtro[1] &&
        filtros.push({
          clave: filtro[0],
          valor: typeof filtro[1] === 'object' ? filtro[1].valor : filtro[1],
          descripcion: typeof filtro[1] === 'object' ? filtro[1].etiqueta : filtro[1],
          etiqueta: filtrosBusqueda[filtro[0] as keyof TFormInputs].etiqueta,
        })
    })
    dispatch(actualizarFiltros(filtros))
    onHide()
    dispatch(establecerPagina(1))
  }

  return (
    <>
      <Offcanvas
        show={show}
        onHide={onHide}
        scroll={true}
        backdrop={true}
        onEntering={() => {
          handleOnEntering()
          setEnvioFormEnter(false)
        }}
        placement={'end'}
        onExit={envioFormEnter && handleSubmit(onSubmit)}>
        <Offcanvas.Header closeButton className='offcanvas-header-separator'>
          <div className='d-flex flex-column'>
            <h2>Filtros</h2>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='mb-10'>
              <Controller
                control={control}
                name='nombre'
                render={({ field: { onChange } }) => (
                  <FormInput
                    className='mb-10'
                    nombreCampo='nombre'
                    nombre='Nombre'
                    register={register}
                    placeholder='Ej. Querétaro'
                    isObligatorio={false}
                    tipoFiltro='texto'
                    onChange={(e: Event) => {
                      onChange(e)
                    }}
                  />
                )}
              />
            </div>
            <div className='mb-10'>
              <Controller
                control={control}
                name='razonSocial'
                render={({ field: { onChange } }) => (
                  <FormInput
                    className='mb-10'
                    nombreCampo='razonSocial'
                    nombre='Razón social'
                    register={register}
                    placeholder='Ej. Veolia Agua Aguascalientes México'
                    isObligatorio={false}
                    onChange={(e: Event) => {
                      onChange(e)
                    }}
                  />
                )}
              />
            </div>
            <div className='mb-10'>
              <Controller
                control={control}
                name='rfc'
                render={({ field: { onChange } }) => (
                  <FormInput
                    className='mb-10'
                    nombreCampo='rfc'
                    nombre='RFC'
                    register={register}
                    placeholder='Ej. PMA940906T62'
                    isObligatorio={false}
                    onChange={(e: Event) => {
                      onChange(e)
                    }}
                  />
                )}
              />
            </div>
            <div className='mb-10'>
              <Controller
                control={control}
                name='estado'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    value={value}
                    nombre='Estado'
                    elementos={catEstados}
                    onChange={(e: Function) => {
                      onChange(e)
                    }}
                    placeholder='Ej. Aguascalienntes'
                  />
                )}
              />
            </div>
            <div className='d-flex justify-content-end'>
              <button className='btn me-2' type='button' onClick={onHide}>
                Cancelar
              </button>
              <button className='btn btn-primary' type='submit'>
                Buscar <i className='fa-solid fa-arrow-right-long ps-2'></i>
              </button>
            </div>
          </form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  )
}
